import type { Tone } from './AccountUi'

/* What a booking's progress and a month of a bank plan are called on the
   buyer's side, and how each of them reads.

   Its own module rather than a few more exports from AccountUi: that file
   exports components, and a module exporting both a component and a plain
   value breaks fast refresh — the same reason toast-context sits apart from
   the toast itself.

   The words are not the ones the console uses. A buyer's "pending" is our
   "we have not checked the receipt yet", which is worth saying in words
   rather than leaving as a status they have to interpret. */

/** What each booking status is called on this side of the desk, and how it
    reads. A buyer's "pending" is our "we have not checked the receipt yet",
    which is worth saying in words rather than leaving as a status word they
    have to interpret. */
export const BOOKING_STATUS: Record<string, { label: string; tone: Tone; hint: string }> = {
  pending: {
    label: 'Awaiting check',
    tone: 'waiting',
    hint: 'We are confirming your payment. Nothing needed from you.',
  },
  verified: {
    label: 'Payment verified',
    tone: 'positive',
    hint: 'Your payment has landed and the car is held for you.',
  },
  confirmed: {
    label: 'Confirmed',
    tone: 'positive',
    hint: 'The order is confirmed and the car is yours to collect.',
  },
  sold: { label: 'Sold', tone: 'positive', hint: 'The car has been transferred to you.' },
  completed: { label: 'Completed', tone: 'positive', hint: 'Everything on this order is settled.' },
  cancelled: { label: 'Cancelled', tone: 'bad', hint: 'This order was cancelled.' },
}

export const statusOf = (status: string) =>
  BOOKING_STATUS[status] ?? { label: status, tone: 'waiting' as Tone, hint: '' }

/** A month on a bank plan. */
export const INSTALMENT_STATUS: Record<string, { label: string; tone: Tone }> = {
  paid: { label: 'Paid', tone: 'positive' },
  due: { label: 'Due this month', tone: 'attention' },
  overdue: { label: 'Overdue', tone: 'bad' },
  upcoming: { label: 'Upcoming', tone: 'waiting' },
}

/** A calendar day as a person reads one: 14 Aug 2026. */
export function day(value: string) {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value

  return parsed.toLocaleDateString('en-PK', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}
